import CheckIcon from "@mui/icons-material/Check";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { type FC, useEffect, useRef, useState } from "react";
import { ChatActionButton } from "./ChatActionButton";

/** How long the button shows the "copied" check before reverting to the copy icon. */
const COPIED_RESET_MS = 1500;

type Props = {
	/** Plain text placed on the clipboard. */
	text: string;
	/** Accessible name + resting tooltip (e.g. "Copy answer"). */
	label: string;
	/** Accessible name + tooltip shown briefly after a successful copy. */
	copiedLabel: string;
	dataTestId?: string;
	/** Called when the clipboard write fails (denied permission / insecure context) so the parent can
	 *  track/surface it — the failure is never swallowed. */
	onError?: (error: unknown) => void;
};

/**
 * Copy-to-clipboard action for a chat answer, sized for a `ChatActionBar` (which owns the row's
 * hover/focus reveal). Flips to a check + "copied" tooltip for a moment after a copy.
 */
export const ChatCopyButton: FC<Props> = ({ text, label, copiedLabel, dataTestId, onError }) => {
	const [copied, setCopied] = useState(false);
	const resetRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	useEffect(() => () => clearTimeout(resetRef.current), []);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (error) {
			// A blocked clipboard (denied permission / insecure context) is reported to the parent, not swallowed.
			onError?.(error);
			return;
		}
		setCopied(true);
		clearTimeout(resetRef.current);
		resetRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
	};

	return (
		<ChatActionButton
			label={copied ? copiedLabel : label}
			icon={copied ? <CheckIcon color="success" /> : <CopyIcon />}
			onClick={() => void handleCopy()}
			dataTestId={dataTestId}
		/>
	);
};
